import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/shared/contexts/AuthContext'

interface ProtectedRouteProps {
    children: React.ReactNode
    requireAdmin?: boolean
}

/**
 * Client-side route guard replacing Next.js middleware.
 * Redirects unauthenticated users to /login.
 * Optionally checks for admin role.
 */
export function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
    const { user, profile, loading } = useAuth()
    const location = useLocation()

    // Still loading auth state — show nothing to avoid flash
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        )
    }

    // Not authenticated → redirect to login
    if (!user) {
        return <Navigate to="/login" state={{ from: location.pathname }} replace />
    }

    // Needs admin but user is not admin or super_admin
    if (requireAdmin && profile?.role !== 'admin' && profile?.role !== 'super_admin') {
        return <Navigate to="/dashboard" replace />
    }

    return <>{children}</>
}

/**
 * Redirect authenticated users away from public pages (login, register).
 */
export function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
    const { user, loading } = useAuth()

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        )
    }

    // Already logged in → send to dashboard
    if (user) {
        return <Navigate to="/dashboard" replace />
    }

    return <>{children}</>
}
